import { useMemo } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'

import { CrewTemplatesPanel } from '@/components/crews/crew-templates-panel'
import { StaffedCrewsPanel } from '@/components/crews/staffed-crews-panel'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { WorkerLifecycleNav } from '@/components/workers/worker-lifecycle-nav'
import { getServiceInstanceIdFromSearch } from '@/lib/service-instance'

type CrewTab = 'templates' | 'staffed'

const isCrewTab = (value: string | null): value is CrewTab =>
  value === 'templates' || value === 'staffed'

export default function CrewsPage() {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const activeTab = useMemo(() => {
    const tab = searchParams.get('tab')
    return isCrewTab(tab) ? tab : 'templates'
  }, [searchParams])

  const handleTabChange = (value: string) => {
    if (!isCrewTab(value)) {
      return
    }
    const nextParams = new URLSearchParams(searchParams)
    if (value === 'templates') {
      nextParams.delete('tab')
    } else {
      nextParams.set('tab', value)
    }
    setSearchParams(nextParams, { replace: true })
  }

  return (
    <div className="space-y-6">
      <WorkerLifecycleNav />
      <div>
        <h1 className="text-2xl font-semibold">Teams</h1>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="templates">Teams</TabsTrigger>
          <TabsTrigger value="staffed">Staffed teams</TabsTrigger>
        </TabsList>
        <TabsContent value="templates" className="mt-4">
          <CrewTemplatesPanel
            serviceInstanceId={serviceInstanceId}
            onManageMembers={(templateId) => {
              const nextParams = new URLSearchParams(searchParams)
              nextParams.set('tab', 'staffed')
              nextParams.set('template_id', templateId)
              nextParams.delete('crew_id')
              setSearchParams(nextParams)
            }}
          />
        </TabsContent>
        <TabsContent value="staffed" className="mt-4">
          <StaffedCrewsPanel
            serviceInstanceId={serviceInstanceId}
            focusTemplateId={searchParams.get('template_id')}
            onFocusHandled={() => {
              const nextParams = new URLSearchParams(searchParams)
              nextParams.delete('template_id')
              nextParams.delete('crew_id')
              setSearchParams(nextParams, { replace: true })
            }}
          />
        </TabsContent>
      </Tabs>
    </div>
  )
}
